<template>
  <div class="cancellation-reason-form" v-show="showForm">
    <div class="row">
      <div class="sc-label">
        Cancellation Requested For?<span class="required">*</span>
      </div>
      <div class="sc-help">
        Select the applicant the cancellation reason applies to.
      </div>
      <div class="applicant-options-container">
        <div class="applicant-options">
          <div
            class="applicant-option"
            v-for="applicant in applicants"
            :key="applicant.id"
          >
            <Field
              ref="selectedApplicantRefs"
              name="selectedApplicantId"
              :disabled="savingChanges"
              v-slot="{ field }"
              :uncheckedValue="null"
              :checkedValue="Number(applicant.id)"
              :checked="Number(selectedApplicantId) === Number(applicant.id)"
              v-model="selectedApplicantId"
            >
              <label class="xradio label-text">
                <div class="radio-text">
                  {{ applicant.fullName }} - {{ applicant.applicantTypeName }}
                </div>
                <input
                  v-bind="field"
                  type="radio"
                  :value="applicant.id"
                  :checked="
                    Number(selectedApplicantId) === Number(applicant.id)
                  "
                  @change="handleChange(Number(applicant.id), $event)"
                />
                <span></span>
              </label>
            </Field>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="sc-label">
        Cancellation Reason<span class="required">*</span>
      </div>
      <div class="sc-help">
        Please select a reason for the cancellation from the list below.
      </div>

      <div class="sc-label">Cancellation Reason List</div>
      <div class="sc-form-field">
        <Field name="selectedReasonId" v-slot="{ field }">
          <select
            v-bind="field"
            :disabled="savingChanges"
            :title="selectedReason ? selectedReason.description : undefined"
          >
            <option
              v-for="reason in cancellationReasons"
              :key="reason.id"
              :value="reason.id"
            >
              {{ reason.description }}
            </option>
          </select>
        </Field>
        <ErrorMessage class="error-message" name="selectedReasonId" />
      </div>
    </div>
    <div class="row footer xdropdown-buttons sc-dropdown-buttons">
      <button
        v-show="!savingChanges"
        class="invert-color"
        @click.prevent="cancel"
        :disabled="savingChanges"
      >
        <span class="button-text">Cancel</span>
      </button>
      <button
        @click.prevent="submit"
        :class="[{ loading: savingChanges }]"
        :disabled="!isFormValid || savingChanges"
      >
        <span class="button-text">Save Changes</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, toRef, toRefs, onMounted, watch } from "vue";
import { Field, ErrorMessage, useForm } from "vee-validate";
import * as yup from "yup";
import { Type, LOSType } from "@/types/types";
import { useTypeStore } from "@/store/store/typeStore";
import { useApplicationStore } from "@/store/store/applicationVMStore";
import { ApplicantVM } from "@/types/applicant";
import { ApplicationStatusIds } from "@/types/application";

const applicationStoreV2 = useApplicationStore();
const typeStore = useTypeStore();

const props = defineProps<{
  statusId: number | null | undefined;
	initialStatusId: number;
}>();

const { statusId } = toRefs(props);

const emit = defineEmits<{
  (event: "cancel"): void;
  (event: "submit", value?: number): void;
}>();

const validationSchema = computed(() => {
  return yup.object().shape({
    selectedApplicantId: yup.number().required("An applicant is required"),
    selectedReasonId: yup.number().required("A reason is required"),
  });
});

const { values, validate, errors, setFieldValue, setFieldTouched } = useForm({
  validationSchema: validationSchema.value,
});

const selectedApplicantRefs = ref<Array<InstanceType<typeof Field>> | null>(
  null
);

const showForm = ref<boolean>(false);

const selectedReasonId = toRef(values, "selectedReasonId");

const selectedApplicantId = toRef(values, "selectedApplicantId");

const savingChanges = ref<boolean>(false);

// Computed Values
const isFormValid = computed<boolean>(() => {
  return (
    selectedReasonId.value !== undefined &&
    selectedReasonId.value !== null &&
    selectedApplicantId.value !== null &&
    selectedApplicantId.value !== undefined
  );
});

const applicants = computed<Array<ApplicantVM>>(
  () => applicationStoreV2.Applicants
);

const selectedReason = computed<LOSType | undefined>(() => {
  return cancellationReasons.value.find(
    (r: LOSType) => r.id === Number(values.selectedReasonId)
  );
});

const selectedApplicant = computed<ApplicantVM | undefined>(() => {
  return applicants.value.find(
    (r: ApplicantVM) => r.id === Number(values.selectedApplicantId)
  );
});

const cancellationReasons = computed<Array<LOSType>>(() =>
  typeStore.getTypes(Type.ApplicationStatusReasons)
);

const comment = computed<string>(
  () =>
    `Application status updated to Cancelled due to ${selectedReason.value?.description} per ${selectedApplicant.value?.fullName}'s request.`
);

function resetForm() {
  setFieldValue("selectedApplicantId", null);
  setFieldValue("selectedReasonId", null);

  setFieldTouched("selectedApplicantId", false);
  setFieldTouched("selectedReasonId", false);
}

function cancel() {
  resetForm();
  emit("cancel");
}

function toggleForm(flag: boolean) {
  showForm.value = flag;
}

function setSavingChanges(flag: boolean) {
  savingChanges.value = flag;
}

function submit() {
  savingChanges.value = true;
  emit("submit");
}

function handleChange(applicantId: number, event: any) {
  setFieldValue("selectedApplicantId", Number(applicantId));
}

async function initialize() {
  await typeStore.getType(Type.ApplicationStatusReasons);
  if (applicants.value.length === 1) {
    setFieldValue("selectedApplicantId", applicants.value[0].id);
  }
}

onMounted(async () => {
  await initialize();
});

watch(statusId, (newValue: any) => {
  if (newValue === ApplicationStatusIds.CANCELLED && props.initialStatusId !== ApplicationStatusIds.DECLINED) {
    initialize();
  } else {
    resetForm();
    showForm.value = false;
  }
});

defineExpose({
  selectedReasonId,
  selectedApplicantId,
  selectedReason,
  comment,
  validate,
  setSavingChanges,
  toggleForm,
  resetForm,
});
</script>

<style scoped src="@/assets/css/inputs/sc-radio.css"></style>
<style scoped src="@/assets/css/buttons/btn-loading.css"></style>
<style
  scoped
  src="@/assets/css/application-status/cancellation-reason-form.css"
></style>
